// Dashboard.js - Версия с добавлением "Инструменты VK"

import { useState, useEffect, useRef } from 'react';
import { useNavigate, Routes, Route, Link, useLocation } from 'react-router-dom';
import {
    Home,
    User,
    DollarSign,
    Grid,
    Users,
    BookOpen,
    FileText,
    Menu,
    X,
    LogOut,
    Bell,
    Settings,
    ChevronLeft,
    ChevronRight,
    Wallet,
    UserPlus,
    Calendar,
    TrendingUp,
    ArrowLeftRight,
    Wrench
} from 'lucide-react';

// Импорт страниц разделов
import DashboardHome from './DashboardHome';
import ProfilePage from '../components/ProfilePage';
import FinancePage from '../FinancePage';
import ClustersPage from './ClustersPage';
import PartnersPage from '../components/PartnersPage';
import MaterialsPage from '../MaterialsPage';
import P2PExchange from '../components/P2PExchange';
import LightMixCluster from '../components/LightMixCluster';


// Импорт компонентов кластеров
import LightBoxCluster from '../components/LightBoxCluster';
import LightCaseCluster from '../components/LightCaseCluster';
import LightLotoCluster from '../components/LightLotoCluster';

import ChainAnalysis from '../components/ChainAnalysis';


import { authService } from '../services/authService';

// ✅ НОВЫЙ ИМПОРТ: useAuth
import { useAuth } from '../contexts/AuthContext';

const Dashboard = ({ onLogout }) => {
    // ✅ ИСПОЛЬЗУЕМ useAuth ВМЕСТО ЛОКАЛЬНОГО СОСТОЯНИЯ
    const { user, loading: authLoading, logout } = useAuth();

    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const [recentReferrals, setRecentReferrals] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [vkToolsLink, setVkToolsLink] = useState(null); // ✅ НОВОЕ: Состояние для ссылки VK Tools
    const [vkToolsLoading, setVkToolsLoading] = useState(false); // ✅ НОВОЕ: Состояние загрузки

    const navigate = useNavigate();
    const location = useLocation();
    const notificationsRef = useRef(null);

    // ✅ ИСПРАВЛЕНО: Загружаем только рефералов из данных пользователя
    useEffect(() => {
        if (user?.team_statistics?.recent_partners) {
            console.log('✅ Инициализация рефералов из AuthContext');

            const recent = user.team_statistics.recent_partners
                .slice(0, 5) // Ограничиваем 5 последними
                .filter(partner => {
                    // Показываем только рефералов за последние 7 дней
                    const partnerDate = new Date(partner.registration_date);
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return partnerDate >= weekAgo;
                });

            setRecentReferrals(recent);
            setUnreadCount(recent.length);
        }
    }, [user]);

    // ✅ НОВОЕ: Получение ссылки на VK Tools
    useEffect(() => {
        const fetchVkToolsLink = async () => {
            if (!user?.email) return;

            setVkToolsLoading(true);
            try {
                const response = await fetch('https://online-lead.ru/remote-auth-merchant-checked', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams({
                        project: 'b39e9a83-982b-4c50-9a25-447a10e9cb16',
                        email: user.email,
                        v: '5.120'
                    })
                });

                const data = await response.json();

                if (data?.body?.[0]?.link) {
                    setVkToolsLink(`${data.body[0].link}&m=${user.email}`);
                    console.log('✅ VK Tools ссылка получена:', data.body[0].link);
                } else {
                    console.error('❌ Не удалось получить ссылку VK Tools:', data);
                }
            } catch (error) {
                console.error('❌ Ошибка при получении ссылки VK Tools:', error);
            } finally {
                setVkToolsLoading(false);
            }
        };

        fetchVkToolsLink();
    }, [user?.email]);

    // Закрытие уведомлений при клике вне
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
                setIsNotificationsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Эффект для закрытия бокового меню на мобильных при изменении маршрута
    useEffect(() => {
        setIsMobileSidebarOpen(false);
    }, [location.pathname]);

    // Проверка активности ссылки
    const isActive = (path) => {
        if (path === '/dashboard') {
            return location.pathname === '/dashboard';
        }
        return location.pathname.startsWith(path);
    };

    // ✅ ИСПРАВЛЕНО: Выход из аккаунта
    const handleLogout = () => {
        logout(); // Используем logout из AuthContext
        if (onLogout) onLogout();
        navigate('/');
    };

    // Открытие/закрытие уведомлений
    const toggleNotifications = () => {
        setIsNotificationsOpen(!isNotificationsOpen);
        if (!isNotificationsOpen) {
            // При открытии уведомлений сбрасываем счетчик непрочитанных
            setTimeout(() => setUnreadCount(0), 1000);
        }
    };

    // Форматирование времени регистрации
    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 1) {
            return 'вчера';
        } else if (diffDays <= 7) {
            return `${diffDays} дней назад`;
        } else {
            return date.toLocaleDateString('ru-RU');
        }
    };

    // Пункты меню
    const menuItems = [
        {
            name: 'Главная',
            path: '/dashboard',
            icon: <Home size={20} />,
            isReady: true
        },
        {
            name: 'Профиль',
            path: '/dashboard/profile',
            icon: <User size={20} />,
            isReady: true
        },
        {
            name: 'Финансы',
            path: '/dashboard/finance',
            icon: <DollarSign size={20} />,
            isReady: true
        },
        {
            name: 'P2P Обмен',
            path: '/dashboard/p2p',
            icon: <ArrowLeftRight size={20} />,
            isReady: true
        },
        {
            name: 'Кластеры',
            path: '/dashboard/clusters',
            icon: <Grid size={20} />,
            isReady: true
        },
        {
            name: 'Анализ цепочек',
            path: '/dashboard/chain-analysis',
            icon: <TrendingUp size={20} />,
            isReady: true
        },
        {
            name: 'Партнёры',
            path: '/dashboard/partners',
            icon: <Users size={20} />,
            isReady: true
        },
        {
            name: 'Материалы',
            path: '/dashboard/materials',
            icon: <BookOpen size={20} />,
            isReady: true
        },
        // ✅ НОВОЕ: Пункт меню "Инструменты VK"
        {
            name: 'Инструменты VK',
            path: vkToolsLink,
            icon: <Wrench size={20} />,
            isReady: true,
            isExternal: true, // Флаг для внешних ссылок
            loading: vkToolsLoading
        }
    ];
    // Показываем загрузку, если данные пользователя еще не загружены
    if (authLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Загрузка...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-gray-50 overflow-hidden">
            {/* Боковое меню для десктопа */}
            <aside className={`hidden md:flex flex-col fixed left-0 top-0 h-full bg-white border-r border-gray-200 transition-all duration-300 z-30 ${isSidebarOpen ? 'w-64' : 'w-16'}`}>
                {/* Шапка сайдбара */}
                <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
                    {isSidebarOpen && (
                        <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-r from-slate-800 to-slate-600 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-sm">Л</span>
                            </div>
                            <span className="ml-2 font-bold text-gray-800">ЛАЙТ</span>
                        </div>
                    )}
                    <button
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        {isSidebarOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
                    </button>
                </div>

                {/* Навигация */}
                <nav className="flex-1 px-2 py-4 overflow-y-auto">
                    <ul className="space-y-1">
                        {menuItems.map((item, index) => {
                            // ✅ НОВОЕ: Обработка внешних ссылок
                            if (item.isExternal) {
                                return (
                                    <li key={index}>
                                        {item.loading ? (
                                            <div className={`flex items-center px-4 py-3 rounded-lg text-gray-400 ${!isSidebarOpen && 'justify-center'}`}>
                                                <span className="mr-3">{item.icon}</span>
                                                {isSidebarOpen && (
                                                    <span className="flex-1 font-medium">Загрузка...</span>
                                                )}
                                            </div>
                                        ) : item.path ? (
                                            <a
                                                href={item.path}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`flex items-center px-4 py-3 rounded-lg transition-colors text-gray-600 hover:bg-slate-50 ${!isSidebarOpen && 'justify-center'}`}
                                            >
                                                <span className="mr-3">{item.icon}</span>
                                                {isSidebarOpen && (
                                                    <span className="flex-1 font-medium">{item.name}</span>
                                                )}
                                            </a>
                                        ) : (
                                            <div className={`flex items-center px-4 py-3 rounded-lg text-gray-400 cursor-not-allowed ${!isSidebarOpen && 'justify-center'}`}>
                                                <span className="mr-3">{item.icon}</span>
                                                {isSidebarOpen && (
                                                    <span className="flex-1 font-medium">{item.name}</span>
                                                )}
                                            </div>
                                        )}
                                    </li>
                                );
                            }

                            // Обычные внутренние ссылки
                            return (
                                <li key={index}>
                                    <Link
                                        to={item.path}
                                        className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                                            isActive(item.path)
                                                ? 'bg-slate-800 text-white'
                                                : 'text-gray-600 hover:bg-slate-50'
                                        } ${!item.isReady ? 'opacity-75' : ''} ${!isSidebarOpen && 'justify-center'}`}
                                    >
                                        <span className="mr-3">{item.icon}</span>
                                        {isSidebarOpen && (
                                            <>
                                                <span className="flex-1 font-medium">{item.name}</span>
                                                {!item.isReady && (
                                                    <span className="ml-auto px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
                                                        Скоро
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </nav>

                {/* Профиль пользователя в сайдбаре */}
                {user && (
                    <div className="p-4 border-t border-gray-200">
                        <Link
                            to="/dashboard/profile"
                            className={`flex items-center ${isSidebarOpen ? 'justify-start' : 'justify-center'} hover:bg-gray-50 rounded-lg p-2 transition-colors`}
                        >
                            <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-r from-slate-800 to-slate-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
                                {user.avatar ? (
                                    <img src={user.avatar} alt={user.login} className="w-10 h-10 rounded-full object-cover" />
                                ) : (
                                    (user.first_name || user.login || 'U').charAt(0).toUpperCase()
                                )}
                            </div>
                            {isSidebarOpen && (
                                <div className="ml-3 overflow-hidden">
                                    <div className="font-medium text-gray-800 truncate">
                                        {user.first_name && user.last_name
                                            ? `${user.first_name} ${user.last_name}`
                                            : user.login
                                        }
                                    </div>
                                    <div className="text-sm text-gray-500 truncate">{user.email}</div>
                                </div>
                            )}
                        </Link>
                    </div>
                )}

                {/* Кнопка выхода */}
                <div className="p-4 border-t border-gray-200">
                    <button
                        onClick={handleLogout}
                        className={`flex items-center w-full px-4 py-3 text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors ${!isSidebarOpen && 'justify-center'}`}
                    >
                        <LogOut size={20} className="mr-3" />
                        {isSidebarOpen && <span className="font-medium">Выход</span>}
                    </button>
                </div>
            </aside>

            {/* Мобильное боковое меню */}
            <div className={`fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden transition-opacity ${isMobileSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setIsMobileSidebarOpen(false)}>
                <div className={`fixed left-0 top-0 h-full w-64 bg-white transform transition-transform ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} onClick={(e) => e.stopPropagation()}>
                    {/* Шапка мобильного меню */}
                    <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100">
                        <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-r from-slate-800 to-slate-600 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-sm">Л</span>
                            </div>
                            <span className="ml-2 font-bold text-gray-800">ЛАЙТ</span>
                        </div>
                        <button
                            onClick={() => setIsMobileSidebarOpen(false)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Профиль пользователя в мобильном меню */}
                    {user && (
                        <div className="px-4 py-4 border-b border-gray-100">
                            <div className="flex items-center">
                                <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-r from-slate-800 to-slate-600 flex items-center justify-center text-white font-semibold">
                                    {user.avatar ? (
                                        <img src={user.avatar} alt={user.login} className="w-10 h-10 rounded-full object-cover" />
                                    ) : (
                                        (user.first_name || user.login || 'U').charAt(0).toUpperCase()
                                    )}
                                </div>
                                <div className="ml-3">
                                    <div className="font-medium text-gray-800">
                                        {user.first_name && user.last_name
                                            ? `${user.first_name} ${user.last_name}`
                                            : user.login
                                        }
                                    </div>
                                    <div className="text-sm text-gray-500">{user.email}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Навигация мобильного меню */}
                    <nav className="flex-1 px-2 py-4">
                        <ul className="space-y-1">
                            {menuItems.map((item, index) => {
                                // ✅ НОВОЕ: Обработка внешних ссылок в мобильном меню
                                if (item.isExternal) {
                                    return (
                                        <li key={index}>
                                            {item.loading ? (
                                                <div className="flex items-center px-4 py-3 rounded-lg text-gray-400">
                                                    <span className="mr-3">{item.icon}</span>
                                                    <span className="flex-1 font-medium">Загрузка...</span>
                                                </div>
                                            ) : item.path ? (
                                                <a
                                                    href={item.path}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center px-4 py-3 rounded-lg transition-colors text-gray-600 hover:bg-slate-50"
                                                    onClick={() => setIsMobileSidebarOpen(false)}
                                                >
                                                    <span className="mr-3">{item.icon}</span>
                                                    <span className="flex-1 font-medium">{item.name}</span>
                                                </a>
                                            ) : (
                                                <div className="flex items-center px-4 py-3 rounded-lg text-gray-400 cursor-not-allowed">
                                                    <span className="mr-3">{item.icon}</span>
                                                    <span className="flex-1 font-medium">{item.name}</span>
                                                </div>
                                            )}
                                        </li>
                                    );
                                }

                                // Обычные внутренние ссылки
                                return (
                                    <li key={index}>
                                        <Link
                                            to={item.path}
                                            className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                                                isActive(item.path)
                                                    ? 'bg-slate-800 text-white'
                                                    : 'text-gray-600 hover:bg-slate-50'
                                            } ${!item.isReady ? 'opacity-75' : ''}`}
                                            onClick={() => setIsMobileSidebarOpen(false)}
                                        >
                                            <span className="mr-3">{item.icon}</span>
                                            <span className="flex-1 font-medium">{item.name}</span>
                                            {!item.isReady && (
                                                <span className="ml-auto px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
                                                    Скоро
                                                </span>
                                            )}
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    </nav>

                    {/* Кнопка выхода в мобильном меню */}
                    <div className="p-4 border-t border-gray-100">
                        <button
                            onClick={handleLogout}
                            className="flex items-center w-full px-4 py-3 text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
                        >
                            <LogOut size={20} className="mr-3" />
                            <span className="font-medium">Выход</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Основной контент */}
            <div className={`flex-1 flex flex-col ${isSidebarOpen ? 'md:ml-64' : 'md:ml-16'} transition-all duration-300 w-full`}>
                {/* Хедер */}
                <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
                    <div className="px-4 py-4">
                        <div className="flex items-center justify-between">
                            {/* Кнопка мобильного меню */}
                            <button
                                onClick={() => setIsMobileSidebarOpen(true)}
                                className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <Menu size={24} />
                            </button>

                            {/* Заголовок страницы */}
                            <div className="flex-1">
                                <h1 className="text-xl md:text-2xl font-bold text-gray-800">
                                    {menuItems.find(item => !item.isExternal && isActive(item.path))?.name || 'Панель управления'}
                                </h1>
                            </div>

                            {/* Уведомления */}
                            <div className="relative" ref={notificationsRef}>
                                <button
                                    onClick={toggleNotifications}
                                    className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <Bell size={22} />
                                    {unreadCount > 0 && (
                                        <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                                            {unreadCount}
                                        </span>
                                    )}
                                </button>

                                {/* Панель уведомлений */}
                                {isNotificationsOpen && (
                                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
                                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                            <h3 className="font-semibold text-gray-800">Уведомления</h3>
                                        </div>
                                        <div className="max-h-96 overflow-y-auto">
                                            {recentReferrals.length > 0 ? (
                                                recentReferrals.map((referral, index) => (
                                                    <div
                                                        key={index}
                                                        className="px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                                                    >
                                                        <div className="flex items-start">
                                                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                                                <UserPlus className="text-green-600" size={18} />
                                                            </div>
                                                            <div className="ml-3 flex-1 min-w-0">
                                                                <p className="text-sm font-medium text-gray-800">
                                                                    Новый партнёр
                                                                </p>
                                                                <p className="text-sm text-gray-600 truncate">
                                                                    {referral.full_name || referral.login}
                                                                </p>
                                                                <p className="text-xs text-gray-400 mt-1">
                                                                    {formatTimeAgo(referral.registration_date)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="px-4 py-8 text-center text-gray-500">
                                                    <Bell className="mx-auto mb-2 text-gray-400" size={32} />
                                                    <p className="text-sm">
                                                        Нет новых уведомлений
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Контент страницы */}
                <main className="flex-1 p-6 overflow-auto">
                    <Routes>
                        <Route path="/" element={<DashboardHome />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="/finance" element={<FinancePage />} />
                        <Route path="/clusters" element={<ClustersPage />} />
                        <Route path="/p2p" element={<P2PExchange />} />
                        <Route path="/clusters/lite-box" element={<LightBoxCluster />} />
                        <Route path="/clusters/lite-case" element={<LightCaseCluster />} />
                        <Route path="/clusters/lite-loto" element={<LightLotoCluster />} />
                        <Route path="/chain-analysis" element={<ChainAnalysis />} />
                        <Route path="/partners" element={<PartnersPage />} />
                        <Route path="/materials" element={<MaterialsPage />} />
                        <Route path="/clusters/lite-mix" element={<LightMixCluster />} />
                    </Routes>
                </main>
            </div>
        </div>
    );
};

export default Dashboard;
